import {Game, GameStatus, PaginatedGames} from "./model/Game.ts";
import {Team} from "./model/Team.ts";
import {Athlete} from "./model/Athlete.ts";
import {Play} from "./model/Play.ts";

const URL = "http://lineuppm.com" // "http://0.0.0.0:8080"

export type Result<T> =
    | { type: 'success'; value: T }
    | { type: 'error'; error: string };

export class Repository {
    private caches: Map<string, unknown> = new Map();

    public async getTeams(forceNetwork: boolean = false): Promise<Result<Team[]>> {
        return this.get<Team[]>(`${URL}/teams`, forceNetwork);
    }

    public async getGames(forceNetwork: boolean = false): Promise<Result<Game[]>> {
        const result = await this.get<Game[]>(`${URL}/games`, forceNetwork);
        if (result.type === 'success') {
            const games = result.value.map((g) => ({
                ...g,
                status: g.status as GameStatus,
            }));

            return {type: 'success', value: games};
        }
        return result;
    }

    public async getGamesPaginated(
        page: number,
        pageSize: number,
        forceNetwork: boolean = false
    ): Promise<Result<PaginatedGames>> {
        // Fetch all games (frontend pagination)
        const result = await this.getGames(forceNetwork);

        if (result.type === 'error') {
            return result;
        }

        // Paginate in memory
        const allGames = result.value;
        const startIdx = (page - 1) * pageSize;
        const games = allGames.slice(startIdx, startIdx + pageSize);

        return {
            type: 'success',
            value: {
                games,
                totalCount: allGames.length,
                page,
                pageSize,
                totalPages: Math.ceil(allGames.length / pageSize)
            }
        };
    }

    public async getPlays(gameId: string, forceNetwork: boolean = false): Promise<Result<Play[]>> {
        return this.get<Play[]>(`${URL}/plays?gameId=${gameId}`, forceNetwork).then(result => {
            if (result.type === 'success') {
                return {
                    type: 'success',
                    value: result.value.map(play => new Play(
                        play.id,
                        play.sequence,
                        play.game,
                        play.type,
                        play.homeScore,
                        play.awayScore,
                        play.period,
                        play.clock,
                        play.scoringPlay,
                        play.participant1,
                        play.participant2,
                        play.participant3
                    ))
                };
            }
            return result;
        });
    }

    public async getPlayers(forceNetwork: boolean = false): Promise<Result<Athlete[]>> {
        return this.get<Athlete[]>(`${URL}/players`, forceNetwork);
    }

    public async getGame(gameId: string, forceNetwork: boolean = false): Promise<Result<Game>> {
        const gamesResult = await this.getGames(forceNetwork);
        if (gamesResult.type === 'success') {
            const game = gamesResult.value.find(game => game.id === gameId);
            if (game) {
                return {type: 'success', value: game};
            }
            return {type: 'error', error: 'Game not found'};
        }
        return {type: 'error', error: gamesResult.error};
    }

    /**
     * Retrieve data from the cache or fetch from the network.
     * @param url       The URL to fetch from.
     * @param forceNetwork Whether to ignore the cache and force a fresh request.
     */
    public async get<T>(url: string, forceNetwork: boolean = false): Promise<Result<T>> {
        // If we already have something in the cache and not forcing network, return that
        if (!forceNetwork && this.caches.has(url)) {
            const cached = this.caches.get(url) as T;
            return {type: 'success', value: cached};
        }

        // Otherwise, fetch from the network
        return new Promise<Result<T>>((resolve) => {
            networkGet(
                url,
                (response) => {
                    try {
                        const decoded = JSON.parse(response) as T;
                        this.caches.set(url, decoded);
                        resolve({type: 'success', value: decoded});
                    } catch (err) {
                        if (err instanceof Error) {
                            resolve({type: 'error', error: err.message ?? 'Failed to parse JSON'});
                        } else {
                            resolve({type: 'error', error: 'Unknown error'});
                        }
                    }
                },
                (errorMsg) => {
                    resolve({type: 'error', error: errorMsg});
                }
            );
        });
    }
}


function networkGet(
    url: string,
    success: (response: string) => void,
    error: (message: string) => void
) {
    fetch(url)
        .then((res) => {
            if (!res.ok) {
                throw new Error(`Server returned ${res.status}`);
            }
            return res.text();
        })
        .then((text) => success(text))
        .catch((err) => error(err.message ?? 'Unknown error'));
}
